<template>
    <div>
        <v-container fluid>
            <v-card class="pb-2 mb-3">
                <v-overlay :value="loadingCheckCredentials" absolute>
                    <div class="text-center">
                        <v-progress-circular
                            class="my-4"
                            :size="50"
                            width="5"
                            color="primary"
                            indeterminate
                        ></v-progress-circular>
                        <p class="display-1">
                            {{ $t('CheckingCredentials') }}...
                        </p>
                    </div>
                </v-overlay>
                <v-card-title class="py-3">{{ title }}</v-card-title>
                <v-card-text class="pb-0 mb-0">
                    <v-row justify="center" align="center" v-if="loading">
                        <div class="text-center">
                            <div>
                                <v-progress-circular
                                    class="my-4"
                                    :size="50"
                                    width="5"
                                    color="primary"
                                    indeterminate
                                ></v-progress-circular>
                            </div>
                            <p class="display-1">{{ $t('Loading') }}...</p>
                        </div>
                    </v-row>
                    <v-row v-if="!loading">
                        <v-col cols="4" align-self="center">
                            <v-select
                                v-model="providerSelected"
                                :items="providers"
                                :label="$t('Provider')"
                                :error-messages="providerError"
                                item-text="providerName"
                                item-value="id"
                                @change="getProvider"
                                dense
                            >
                            </v-select>
                        </v-col>
                        <v-col cols="8" class="text-right py-1" align-self="center">
                            <v-row no-gutters justify="end">
                                <v-menu bottom right>
                                    <template v-slot:activator="{ on: menu, attrs }">
                                        <v-tooltip top>
                                            <template v-slot:activator="{ on: tooltip }">
                                                <v-btn
                                                    text
                                                    v-bind="attrs"
                                                    v-on="{ ...tooltip, ...menu }"
                                                    color="blue-grey"
                                                >
                                                    <v-icon large>mdi-history</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ $t('Versions') }}</span>
                                        </v-tooltip>
                                    </template>
                                    <v-list>
                                        <v-list-item
                                            v-for="(item, i) in versionList"
                                            :key="i"
                                            @click="changeVersion(item)"
                                        >
                                            <v-list-item-title>{{
                                                item.text
                                            }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                                <v-btn @click="cancel">
                                    {{ $t('Cancel') }}
                                </v-btn>
                                <v-btn
                                    color="primary"
                                    @click="addConfigsOfProvider()"
                                    :loading="add"
                                >
                                    <v-icon left>mdi-plus</v-icon>
                                    {{ $t('AddProvider') }}
                                </v-btn>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-simple-table dense class="mt-0">
                    <thead>
                        <tr>
                            <th class="text-left">
                                <h4>
                                    {{ $t('Provider') }}
                                </h4>
                            </th>
                            <th class="text-left"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="configFieldsIntelligence.length === 0">
                            <td
                                colspan="9999"
                                class="pa-2 pl-4 text-center font-weight-regular text-body-1"
                            >
                                {{ this.$t('NoProvidersConfigured') }}
                            </td>
                        </tr>
                        <tr
                            v-for="(item, index) in configFieldsIntelligence"
                            :key="index"
                        >
                            <td class="pa-2 pl-4">
                                {{ item.Name }}
                            </td>
                            <td class="d-flex align-center justify-center mt-2">
                                <v-tooltip top v-if="item.IsConfigurable">
                                    <template v-slot:activator="{ on }">
                                        <a
                                            v-on="on"
                                            @click.prevent="
                                                editConfigsOfProvider(item)
                                            "
                                            class="px-2"
                                        >
                                            <v-icon color="primary" class="mb-2"
                                                >mdi-pencil</v-icon
                                            >
                                        </a>
                                    </template>
                                    <span>{{ $t('Edit') }}</span>
                                </v-tooltip>

                                <v-tooltip top v-if="item.IsConfigurable">
                                    <template v-slot:activator="{ on }">
                                        <a
                                            v-on="on"
                                            @click.prevent="removeField(item)"
                                            class="px-2"
                                        >
                                            <v-icon color="red" class="mb-2"
                                                >mdi-delete</v-icon
                                            >
                                        </a>
                                    </template>
                                    <span>{{ $t('Delete') }}</span>
                                </v-tooltip>

                                <v-tooltip
                                    top
                                    v-if="
                                        item.DocumentIntellicenceProviderID ==
                                            docECMDocumentIntelligenceProvider.AutorecognitionAPI &&
                                        !loading
                                    "
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <div
                                            v-bind="attrs"
                                            v-on="on"
                                            style="width: min-content"
                                        >
                                            <v-switch
                                                v-bind="attrs"
                                                v-on="on"
                                                v-model="item.IsActive"
                                                color="primary"
                                                :disabled="
                                                    !hasAutorecognitionEnabled
                                                "
                                                @change="
                                                    updateAutorecognitionState(
                                                        item
                                                    )
                                                "
                                                dense
                                                small
                                                class="pa-0 pl-4"
                                            >
                                            </v-switch>
                                        </div>
                                    </template>
                                    <span v-if="hasAutorecognitionEnabled">{{
                                        item.IsActive
                                            ? $t('DisableDocSeriesAI')
                                            : $t('EnableDocSeriesAI')
                                    }}</span>
                                    <span v-else>{{
                                        $t(
                                            'OrganizationHasAutorecognitionDisabled'
                                        )
                                    }}</span>
                                </v-tooltip>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </v-card>
        </v-container>
    </div>
</template>

<script>
import ContentTypeService from '@/services/ContentTypeService';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import DocumentIntelligenceProvidersService from '@/services/DocumentIntelligenceProvidersService.js';
import Utils from '@/ecm/common/Utils';
import DocECMDocumentIntelligenceProvider from '@/ecm/models/contentType/documentIntelligence/DTO/Providers';
import Setting from '@/ecm/models/Setting';
import OrganizationService from '@/services/OrganizationService';

export default {
    name: 'DocumentIntelligenceSettings',
    components: {},
    async created() {
        this.loading = true;
        await this.loadContentTypeNameAndVersions(this.contentTypeId);
        await this.getDocumentsProviders();
        await this.loadDocumentIntelligenceSettings();
        await ContentTypeService.getVersions(this.contentTypeId).then(
            (data) => {
                this.versionList = data;
            }
        );

        this.loading = false;
    },
    data() {
        return {
            contentType: { name: '', versions: [] },
            providers: [],
            loading: false,
            add: false,
            versionList: [],
            configFieldsIntelligence: [],
            contentTypeId: this.$route.params.id,
            providerSelected: null,
            docECMDocumentIntelligenceProvider:
                DocECMDocumentIntelligenceProvider,
            hasAutorecognitionEnabled: false,
            loadingCheckCredentials: false,
            setting: new Setting(),
        };
    },
    mixins: [validationMixin],
    validations: {
        providerSelected: { required },
    },
    computed: {
        title() {
            if (this.contentType.name) {
                return `${this.$t('DocumentIntelligenceProviderSettings')} - ${
                    this.contentType.name
                } ${this.contentType.currentVersion}`;
            } else {
                return `${this.$t('DocumentIntelligenceProviderSettings')}`;
            }
        },
        providerError() {
            const errors = [];
            if (!this.$v.providerSelected.$dirty) return errors;
            !this.$v.providerSelected.required &&
                errors.push(this.$t('ProviderRequired'));
            return errors;
        },
    },
    methods: {
        loadContentTypeNameAndVersions(id) {
            ContentTypeService.GetNameAndVersions(id).then((dto) => {
                this.contentType = {
                    name: dto.name,
                    versions: dto.versions,
                    currentVersion: dto.currentVersion,
                };
                this.versionList = this.contentType.versions;
            });
        },
        getProvider() {
            this.providerSelected = this.providers.find(
                (provider) => provider.id === this.providerSelected
            );
        },

        async verifyCredentials(providerId) {
            this.loadingCheckCredentials = true;
            let isValid = false;

            const data = await OrganizationService.getSettings();
            this.setting = new Setting(data);

            const provider = this.setting.documentIntelligenceParameters.find(
                (item) => item.DocumentIntelligenceProviderID === providerId
            );

            if (provider) {
                const allParametersComplete = provider.Parameters.every(
                    (param) => param.Value && param.Value.trim() !== ''
                );

                if (!allParametersComplete) {
                    Utils.toast.error(this.$t('NoCredentialsDetected'));
                } else {
                    isValid = true;
                }
            } else {
                Utils.toast.error(this.$t('ProviderNotFound'));
            }

            this.loadingCheckCredentials = false;
            return isValid;
        },

        async addConfigsOfProvider() {
            const credentialsValid = await this.verifyCredentials(
                this.providerSelected.id
            );

            if (!credentialsValid) {
                return;
            }

            if (this.checkProviderConfigExistence()) {
                return;
            }

            if (credentialsValid) {
                const path = this.generateConfigPath(this.providerSelected.id);
                this.$router.push({ name: path.name, params: path.params });
            }
        },
        generateConfigPath(providerId, itemId = 0) {
            let routeName = '';
            const params = {
                contentTypeID: this.contentTypeId,
                settingID: itemId,
            };

            switch (providerId) {
            case DocECMDocumentIntelligenceProvider.AzureAI:
                routeName = 'AzureAISettings';
                break;
            case DocECMDocumentIntelligenceProvider.Infomaniak:
                routeName = 'InfomaniakLLMSettings';
                break;
            }
            return { name: routeName, params };
        },
        checkProviderConfigExistence() {
            for (const item of this.configFieldsIntelligence) {
                if (
                    item.DocumentIntellicenceProviderID ===
                    this.providerSelected.id
                ) {
                    Utils.toast.error(this.$t('ProviderSelected'));
                    return true;
                }
            }
            return false;
        },
        getDocumentsProviders() {
            DocumentIntelligenceProvidersService.getProvidersList()
                .then((dto) => {
                    this.providers = dto;
                    this.providerSelected = this.providers[0];
                })
                .catch((error) => {
                    console.error('Error loading options:', error);
                });
        },
        async changeVersion(version) {
            if (this.contentType.id !== version.id) {
                this.loadContentTypeNameAndVersions(version.id);
                this.contentTypeId = version.id;
                this.loadDocumentIntelligenceSettings();
            }
        },
        async editConfigsOfProvider(item) {
            this.loadingCheckCredentials = true;

            const credentialsValid = await this.verifyCredentials(
                item.DocumentIntellicenceProviderID
            );

            if (!credentialsValid) {
                this.loadingCheckCredentials = false;
                return;
            }

            this.loadingCheckCredentials = false;

            const path = this.generateConfigPath(
                item.DocumentIntellicenceProviderID,
                item.Id
            );
            this.$router.push({ name: path.name, params: path.params });
        },
        removeField(item) {
            this.$confirm(this.$t('ItemDeleteConfirmation'), {
                title: this.$t('Delete'),
                icon: 'mdi-alert',
                color: 'red',
                buttonTrueText: this.$t('Delete'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res == 1) {
                    this.loading = true;
                    DocumentIntelligenceProvidersService.deleteDocumentIntelligenceSettings(
                        item.Id
                    )
                        .then((data) => {
                            this.loadDocumentIntelligenceSettings();
                            this.loading = false;
                        })
                        .catch(() => {
                            this.loading = false;
                        });
                }
            });
        },
        async loadDocumentIntelligenceSettings() {
            this.configFieldsIntelligence = [];
            DocumentIntelligenceProvidersService.getDocumentIntelligenceListSettings(
                this.contentTypeId
            )
                .then((data) => {
                    this.configFieldsIntelligence = data;
                    this.hasAutorecognitionEnabled =
                        this.configFieldsIntelligence.find(
                            (x) =>
                                x.DocumentIntellicenceProviderID ==
                                this.docECMDocumentIntelligenceProvider
                                    .AutorecognitionAPI
                        ).LicenseEnabled;
                })
                .then(() => {
                    this.loading = false;
                });
        },
        updateAutorecognitionState(item) {
            this.loading = true;
            var dto = {
                AutorecognitionID: item.Id,
                IsActive: item.IsActive,
            };
            DocumentIntelligenceProvidersService.updateAutorecognitionState(dto)
                .then(() => {
                    this.loading = false;
                    Utils.toast.success(this.$t('UpdatedSuccessfully'));
                })
                .catch(() => {
                    this.loading = false;
                });
        },
        cancel() {
            this.$router.push({
                name: 'ContentTypeList',
            });
        }
    },
};
</script>
